import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShieldAlert, RotateCcw, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface Row {
  id: string;
  user_id: string | null;
  kind: string;
  title: string | null;
  content: string;
  reason: string;
  created_at: string;
  restored_at: string | null;
}

const ModerationQueue = ({ isAdmin }: { isAdmin: boolean }) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const fetchRows = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("rejected_submissions" as any)
      .select("id, user_id, kind, title, content, reason, created_at, restored_at")
      .order("created_at", { ascending: false })
      .limit(100);
    setLoading(false);
    if (error) return;
    setRows((data as any as Row[]) || []);
  };

  useEffect(() => {
    if (isAdmin) fetchRows();
  }, [isAdmin]);

  if (!isAdmin) return null;

  const restore = async (id: string) => {
    setBusy(id);
    const { data, error } = await supabase.functions.invoke("admin-restore-submission", {
      body: { submissionId: id },
    });
    setBusy(null);
    if (error || (data as any)?.error) {
      toast({
        title: "Restore failed",
        description: (data as any)?.error || "Could not restore this submission.",
        variant: "destructive",
      });
      return;
    }
    toast({ title: "Submission restored" });
    fetchRows();
  };


  const remove = async (id: string) => {
    setBusy(id);
    const { error } = await supabase.from("rejected_submissions" as any).delete().eq("id", id);
    setBusy(null);
    if (error) {
      toast({ title: "Delete failed", description: error.message, variant: "destructive" });
      return;
    }
    fetchRows();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-8 rounded-2xl border border-border bg-card p-6 shadow-card"
    >
      <div className="flex items-center gap-2 mb-4">
        <ShieldAlert className="w-5 h-5 text-primary" />
        <h2 className="font-serif text-lg text-foreground">Moderation queue</h2>
        <span className="text-xs text-muted-foreground ml-auto">{rows.length} entries</span>
      </div>
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No rejected submissions.</p>
      ) : (
        <ul className="space-y-3">
          {rows.map((r) => (
            <li key={r.id} className="rounded-lg border border-border p-3">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <Badge variant="outline" className="text-[10px] uppercase">{r.kind}</Badge>
                {r.restored_at && <Badge className="text-[10px]">restored</Badge>}
                <span className="text-[11px] text-muted-foreground ml-auto">
                  {new Date(r.created_at).toLocaleString()}
                </span>
              </div>
              {r.title && <p className="text-sm font-medium text-foreground">{r.title}</p>}
              <p className="text-xs text-muted-foreground mb-2 line-clamp-3 whitespace-pre-wrap break-words">{r.content}</p>
              <p className="text-[11px] text-destructive mb-2">Reason: {r.reason}</p>
              <p className="text-[10px] text-muted-foreground mb-2">User: {r.user_id ?? "—"}</p>
              <div className="flex gap-2">
                {!r.restored_at && r.kind === "review" && (
                  <Button size="sm" variant="outline" disabled={busy === r.id} onClick={() => restore(r.id)} className="gap-1">
                    <RotateCcw className="w-3 h-3" /> Approve & restore
                  </Button>
                )}
                <Button size="sm" variant="ghost" disabled={busy === r.id} onClick={() => remove(r.id)} className="gap-1 text-destructive">
                  <Trash2 className="w-3 h-3" /> Dismiss
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </motion.div>
  );
};

export default ModerationQueue;
